import React, { useContext, useEffect, useRef } from 'react';
import proptypes from 'prop-types';
import { Card, CardContent } from '@one-thd/sui-atomic-components';
import {
  StorePod,
  StorePodAddress,
  StorePodBadge,
  StorePodDistance,
  StorePodHours,
  StorePodLocalize,
  StorePodName,
  StorePodPhoneNumber,
  StorePodLink
} from '@thd-olt-component-react/store-pod';
import { extend } from '@thd-nucleus/data-sources';
import { StoreSearchContext } from '../StoreSearch.context';
import { storeShape } from '../storeShape';
import { FEATURE_STORE_LIST } from '../../constants/constants';

const {
  bool, number, oneOf, string, func
} = proptypes;

export const Pod = ({
  disableScroll,
  index,
  linkType,
  isLocalizedStore,
  hasStorePhoneNumber,
  hasStoreFeatureLinks,
  hasButton,
  hasBadge,
  hostName,
  store,
  trackingParam,
  showAllStoreLocalizeButtons,
  customStoreHandler,
}) => {

  const podRef = useRef();
  const { highlightedStoreId, storePodLocalizeCallback } = useContext(StoreSearchContext);

  const { storeId } = store || {};
  const isHighlightedStore = highlightedStoreId === storeId;
  const isAvailable = !!(store?.services?.loadNGo || store?.services?.toolRental);
  const availableFeatureList = Object.keys(FEATURE_STORE_LIST)
    .filter((key) => (!(key === 'rentals' && !isAvailable)))
    .map((key) => (key === 'rentals' && !isAvailable ? false : FEATURE_STORE_LIST[key]));

  useEffect(() => {
    if (isHighlightedStore) {
      let elementToScroll = document.getElementById('js-results');
      let scrollTop = podRef?.current?.offsetTop - 200;
      if (disableScroll) {
        elementToScroll = window;
        scrollTop += 600;
      }

      if (elementToScroll) {
        elementToScroll.scroll({
          top: scrollTop,
          behavior: 'smooth'
        });
      }
    }
  }, [highlightedStoreId]);

  return (
    <div className="sui-mb-3" data-testid="store-search-pod-testId">
      <div ref={podRef} />
      <Card className="sui-h-full" id={`podCardId${index}`} tabindex="0">
        <CardContent disablePadding grow>
          <StorePod key={storeId} store={store}>
            {isLocalizedStore && hasBadge && (
              <StorePodBadge />
            )}
            <div className="sui-pt-1 sui-pb-2">
              <StorePodName label={`${index} - `} linkType={linkType} variant="h5" weight="bold" hostName={hostName} />
            </div>
            <div className="sui-pt-2 sui-pb-2 sui-items-start sui-flex sui-justify-between">
              <StorePodAddress />
              <StorePodDistance />
            </div>
            {hasStorePhoneNumber && (
              <div className="sui-pt-2 sui-pb-2">
                <div className="sui-pb-1">
                  <StorePodPhoneNumber
                    colorOfNumber="alternate"
                    colorOfLabel="alternate"
                    type="store"
                  />
                </div>
                <div className="sui-pb-1">
                  <StorePodPhoneNumber
                    colorOfNumber="alternate"
                    colorOfLabel="alternate"
                    type="tool"
                  />
                </div>
                <StorePodPhoneNumber
                  colorOfNumber="alternate"
                  colorOfLabel="alternate"
                  type="pro"
                />
              </div>
            )}
            <div className="sui-pt-2 sui-pb-2">
              <StorePodHours />
            </div>
            {(!isLocalizedStore || showAllStoreLocalizeButtons) && hasButton && (
              <div className="sui-pt-2">
                <StorePodLocalize dark onClick={storePodLocalizeCallback} customStoreHandler={customStoreHandler} />
              </div>
            )}
            { hasStoreFeatureLinks && availableFeatureList.map((feature) => (
              <div className="sui-pt-2">
                <StorePodLink type="feature" featureData={feature} trackingParam={trackingParam} />
              </div>

            ))}
          </StorePod>
        </CardContent>
      </Card>
    </div>
  );
};

Pod.propTypes = {
  disableScroll: bool,
  index: number.isRequired,
  hasStorePhoneNumber: bool,
  hasStoreFeatureLinks: bool,
  hasButton: bool,
  hasBadge: bool,
  isLocalizedStore: bool,
  linkType: oneOf(['directions', 'localAd', 'rentals', 'services', 'store', 'gardenCenter', 'proDesk']),
  store: storeShape.isRequired,
  hostName: string,
  trackingParam: string,
  showAllStoreLocalizeButtons: bool,
  customStoreHandler: func
};

Pod.defaultProps = {
  disableScroll: false,
  hasStorePhoneNumber: true,
  isLocalizedStore: false,
  linkType: 'store',
  hasStoreFeatureLinks: false,
  hasButton: true,
  hasBadge: true,
  hostName: '',
  trackingParam: '',
  showAllStoreLocalizeButtons: false,
  customStoreHandler: () => {}
};

Pod.displayName = 'Pod';

Pod.dataModel = extend(StorePod);
